import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, AlertCircle } from "lucide-react";
import { formatApiError } from "@/utils/httpErrors";

export function DataRetentionPage() {
  const [formState, setFormState] = useState({
    email: "",
    reason: "",
    submitted: false,
    error: null as string | null,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormState((prev) => ({ ...prev, error: null }));

    if (!formState.email) {
      setFormState((prev) => ({ ...prev, error: "Email is required" }));
      return;
    }

    try {
      const response = await fetch("/api/v1/privacy/deletion-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formState.email,
          reason: formState.reason,
        }),
      });

      if (response.status === 202) {
        setFormState({ email: "", reason: "", submitted: true, error: null });
        setTimeout(() => {
          setFormState((prev) => ({ ...prev, submitted: false }));
        }, 5000);
      } else if (response.status === 400) {
        const data = await response.json();
        setFormState((prev) => ({ ...prev, error: data.detail || "Invalid request" }));
      } else {
        setFormState((prev) => ({ ...prev, error: "Failed to submit deletion request" }));
      }
    } catch (err) {
      setFormState((prev) => ({
        ...prev,
        error: formatApiError(err),
      }));
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-200 px-6 py-8">
        <div className="max-w-3xl mx-auto">
          <Link to="/" className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4">
            <ArrowLeft className="h-4 w-4" />
            Back to platform
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Data Retention & Deletion</h1>
          <p className="text-gray-600 mt-2">Understand how long we retain your data and how to request deletion</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-12 space-y-12 text-gray-700">
        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Retention Schedule</h2>
          <p className="mb-4">
            We follow a tiered retention schedule based on data type and legal requirements:
          </p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300 text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 p-3 text-left font-semibold">Data Type</th>
                  <th className="border border-gray-300 p-3 text-left font-semibold">Retention Period</th>
                  <th className="border border-gray-300 p-3 text-left font-semibold">Legal Basis</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 p-3 font-medium">Engagement documents & analyses</td>
                  <td className="border border-gray-300 p-3">90 days after engagement close</td>
                  <td className="border border-gray-300 p-3">Client service retention</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 p-3 font-medium">Tax consent records (§7216)</td>
                  <td className="border border-gray-300 p-3">7 years</td>
                  <td className="border border-gray-300 p-3">IRS requirement</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-3 font-medium">Audit & security logs</td>
                  <td className="border border-gray-300 p-3">3 years</td>
                  <td className="border border-gray-300 p-3">Compliance & security</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 p-3 font-medium">Account data (post-termination)</td>
                  <td className="border border-gray-300 p-3">30 days after account closure</td>
                  <td className="border border-gray-300 p-3">Service wind-down</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 p-3 font-medium">AI inference logs (Anthropic)</td>
                  <td className="border border-gray-300 p-3">NOT retained</td>
                  <td className="border border-gray-300 p-3">Service provider policy</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Automatic Deletion</h3>
              <p className="text-sm">
                We automatically delete engagement data 90 days after an engagement closes. You will receive a notification 30 days before the deletion occurs, allowing you to request data export if needed.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Request Data Deletion</h2>
          <p className="mb-6">
            You can request deletion of your personal data at any time (subject to legal retention requirements). We will process your request within 45 days and provide confirmation of deletion.
          </p>

          <form onSubmit={handleSubmit} className="max-w-md space-y-4">
            {formState.error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
                {formState.error}
              </div>
            )}

            {formState.submitted && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-700">
                ✓ Deletion request submitted. We'll process it within 45 days and send confirmation to your email.
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                required
                value={formState.email}
                onChange={(e) => setFormState({ ...formState, email: e.target.value })}
                placeholder="you@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">We'll use this to contact you about your request</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for deletion (optional)
              </label>
              <textarea
                value={formState.reason}
                onChange={(e) => setFormState({ ...formState, reason: e.target.value })}
                placeholder="e.g., engagement ended, no longer need account, etc."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>

            <button
              type="submit"
              disabled={formState.submitted}
              className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition text-sm"
            >
              {formState.submitted ? "Request sent..." : "Request data deletion"}
            </button>
          </form>

          <p className="text-xs text-gray-500 mt-4">
            Note: We may retain data longer if required by law. Your request will be processed within 45 days.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Export</h2>
          <p className="mb-3">
            Before we delete your data, you can request an export of all personal information we hold about you. This includes:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-2 text-sm">
            <li>Your account profile and settings</li>
            <li>All engagements you were involved in</li>
            <li>Documents uploaded to those engagements</li>
            <li>Analysis results and reports generated</li>
            <li>Audit logs of your actions</li>
          </ul>
          <p className="mt-4 text-sm">
            To request a data export, email{" "}
            <a href="mailto:privacy@yansarconsulting.com" className="text-blue-600 hover:underline">
              privacy@yansarconsulting.com
            </a>
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Questions About Data Retention?</h2>
          <p className="mb-2">
            If you have questions about our data retention practices, contact us at:
          </p>
          <div className="mt-4 text-sm">
            <p><strong>Privacy Team</strong></p>
            <p><a href="mailto:privacy@yansarconsulting.com" className="text-blue-600 hover:underline">privacy@yansarconsulting.com</a></p>
            <p className="mt-2">Yansar Consulting Services</p>
          </div>
        </section>

        <div className="border-t border-gray-300 pt-6 text-xs text-gray-500">
          <p>Last updated: July 2026</p>
        </div>
      </div>
    </div>
  );
}
